import React, { useState, useRef, useEffect } from "react";

// Offcanvas
import Offcanvas from "react-bootstrap/Offcanvas";

// Components
import Spinner from "../Spinner";
import QuickSearchResults from "./QuickSearchResults";
import QuickSearchButton from "./QuickSearchButton";
import { toast } from "react-toastify";

const QuickSearch = () => {
    let charsBeforeSearch = i18n.nrOfCharsBeforeSearch ?? 3;
    let inputRef = useRef(null);
    const searchTimeout = useRef(null);

    const [searchResults, setSearchResults] = useState(null);
    const [input, setInput] = useState("");
    const [disabled, setDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [tooManyRequests, setTooManyRequests] = useState(false);
    const [show, setShow] = useState(false);
    const matomoTimeout = useRef(null);

    // handle pagination
    const [articlesPage, setArticlesPage] = useState(1);
    const [productsPage, setProductsPage] = useState(1);
    const [seriesPage, setSeriesPage] = useState(1);
    const [pagesPage, setPagesPage] = useState(1);
    const [mediaPage, setMediaPage] = useState(1);
    const [documentsPage, setDocumentsPage] = useState(1);

    const handleShow = () => setShow(true);
    const handleClose = () => setShow(false);
    const setInputFocus = () => inputRef?.focus();
    const prevInputRef = useRef();
    let abortController = useRef(null);

    const handleSetInput = (val) => {
        setInput(val);
        resetPaginations();
    };

    const resetPaginations = () => {
        setArticlesPage(1);
        setProductsPage(1);
        setSeriesPage(1);
        setPagesPage(1);
        setMediaPage(1);
        setDocumentsPage(1);
    };

    const performSearch = async () => {
        setTooManyRequests(false);
        setLoading(true);

        const query =
            "?query=" +
            input +
            "&articlesPage=" +
            articlesPage +
            "&productsPage=" +
            productsPage +
            "&seriesPage=" +
            seriesPage +
            "&pagesPage=" +
            pagesPage +
            "&mediaPage=" +
            mediaPage +
            "&documentsPage=" +
            documentsPage;

        try {
            abortController.current = new AbortController();

            let resp = await fetch(`${i18n.baseUrl}api/quicksearch/search${query}`, {
                signal: abortController.current.signal,
            });

            if (resp.status === 429) {
                setTooManyRequests(true);
            }

            if (resp.ok) {
                setLoading(false);
                if (input?.length < charsBeforeSearch) {
                    setSearchResults(null);
                    resetPaginations();
                } else {
                    let respJson = await resp.json();
                    setSearchResults(respJson);

                    // --- Send event to matomo ---
                    let searchCount = 0;
                    if (respJson.results) {
                        let totalProducts = respJson.results.commerceResults?.totalProducts ?? 0;
                        let totalArticles = respJson.results.commerceResults?.totalArticles ?? 0;
                        let totalSeries = respJson.results.commerceResults?.totalSeries ?? 0;
                        let totalPages = respJson.results.contentResults?.totalPages ?? 0;
                        let totalMedia = respJson.results.contentResults?.totalMedia ?? 0;
                        let totalDocuments = respJson.results.contentResults?.totalDocuments ?? 0;
                        searchCount =
                            totalProducts + totalArticles + totalSeries + totalPages + totalMedia + totalDocuments;
                    }

                    if (window.hasOwnProperty("_mtm")) {
                        matomoTimeout.current = window.setTimeout(() => {
                            window._mtm.push({
                                event: "customSearch",
                                customSearchInput: input,
                                customSearchResults: searchCount,
                            });
                        }, 900);
                    }
                    // --- end send matomo event ---
                }
            } else {
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.log("Error in quicksearch: ", error);
        }
    };

    // search by articlesPage updated
    useEffect(() => {
        clearTimeout(searchTimeout.current);

        // if (prevInputRef.current !== undefined && prevInputRef.current !== input) {
        //     console.log("input was changed from " + prevInputRef.current + " to " + input);
        // }

        searchTimeout.current = setTimeout(() => {
            if (input?.length >= charsBeforeSearch) {
                performSearch();
            } else if (input?.length < charsBeforeSearch) {
                setSearchResults(null);
                setLoading(false);
                abortController.current && abortController.current.abort();
            }
        }, 300);

        prevInputRef.current = input;
    }, [input, articlesPage, productsPage, seriesPage, pagesPage, mediaPage, documentsPage]);

    return (
        <>
            <QuickSearchButton onClick={handleShow} />

            <Offcanvas
                className="custom-offcanvas"
                show={show}
                onShow={setInputFocus}
                onHide={handleClose}
                placement="end"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title></Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body className="c-quick-search">
                    {/* Form */}
                    <form role="search" action="/search/" method="get">
                        <input
                            ref={(node) => {
                                if (node) {
                                    inputRef = node;
                                }
                            }}
                            id="inputsearchDesktop"
                            type="text"
                            name="query"
                            className="form-control form-control-lg"
                            placeholder={i18n.getString("quicksearch.inputplaceholder")}
                            onChange={(e) => handleSetInput(e.target.value)}
                            autoComplete="off"
                            value={input}
                        />
                        <button value="" id="buttonSearchDesktop" type="submit" disabled={disabled}>
                            <i className="icon-search"></i>
                        </button>
                    </form>

                    {/* Too many requests */}
                    {tooManyRequests && (
                        <div className="too-many-requests">
                            Too many requests. Please try again later.
                        </div>
                    )}

                    {/* Loader */}
                    {loading && (
                        <div className="quicksearch-loading-indicator">
                            <Spinner />
                        </div>
                    )}

                    {/* Results */}
                    {searchResults !== null ? (
                        searchResults.results?.commerceResults || searchResults.results?.contentResults ? (
                            <QuickSearchResults
                                resetPaginations={() => resetPaginations()}
                                query={input}
                                results={searchResults.results}
                                articlesPage={articlesPage}
                                productsPage={productsPage}
                                seriesPage={seriesPage}
                                pagesPage={pagesPage}
                                mediaPage={mediaPage}
                                documentsPage={documentsPage}
                                setArticlesPage={(p) => setArticlesPage(p)}
                                setProductsPage={(p) => setProductsPage(p)}
                                setSeriesPage={(p) => setSeriesPage(p)}
                                setPagesPage={(p) => setPagesPage(p)}
                                setMediaPage={(p) => setMediaPage(p)}
                                setDocumentsPage={(p) => setDocumentsPage(p)}
                            />
                        ) : loading ? (
                            ""
                        ) : (
                            <div class="no-results-container">{i18n.getString("quicksearch.nomatchingresults")}</div>
                        )
                    ) : null}
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default React.memo(QuickSearch);
